import React, { useEffect, useState } from 'react'
import { Box, Grid, Stack, Typography, IconButton, Accordion, AccordionSummary, AccordionDetails, CircularProgress, FormControl } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
// icons
import AngleRightIcon from "common/customIcons/angle_right";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// utils and helpers
import { kitRefillLogsDateWise } from "../../../../redux/slices/hyperwolf/kitRefillLogs";
import { openSnackbar } from "../../../../redux/slices/common";
// custom components
import CommonLayout from 'layouts/DashboardLayout'
import NoDataFound from "common/component/NoDataFound";
import DateRange from "components/dateRangePicker/DateRange";

const RefillActivityLogs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const today = dayjs().format('MM/DD/YYYY');

  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [expandedTerminal, setExpandedTerminal] = useState("");
  const [expandedOrder, setExpandedOrder] = useState("");
  const [customFilters, setCustomFilters] = useState({ startDate: today, endDate: today });
  const [dateRange, setDateRangeValue] = useState([null, null]);

  const getOrderQty = (items = []) => items.reduce((sum, product) => sum + (Number(product?.quantity) || 0), 0);

  const handleFetchLogs = async (filters = {}) => {
    try {
      setIsLoading(true);
      const response = await dispatch(kitRefillLogsDateWise(filters)).unwrap();
      setReportData(response || null);
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error;
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning" }));
    } finally {
      setIsLoading(false);
    }
  }

  const setDateRange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRangeValue(dates);
      if (dates[0] && dates[1]) {
        setCustomFilters({
          startDate: dayjs(dates[0]).format('MM/DD/YYYY'),
          endDate: dayjs(dates[1]).format('MM/DD/YYYY'),
        });
      }
    }
  }

  useEffect(() => {
    handleFetchLogs(customFilters);
  }, [customFilters]);

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
              <IconButton onClick={() => navigate(-1)}>
                <AngleRightIcon fontSize="14px" />
              </IconButton>
              <Typography variant='h4' fontWeight="semibold" ml={0.75}>Activity Logs</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2} className="btn-filter-icon">
              {/* Date Filter */}
              <Box className="filter-date-range" style={{ minWidth: "280px" }}>
                <FormControl fullWidth>
                  <DateRange setDateRange={setDateRange} name='range' dateRange={dateRange} />
                </FormControl>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        {!isLoading && reportData?.transactionsScanned !== undefined && (
          <Typography variant='body2' color="text.secondary" mb={2}>
            Transactions Scanned: {reportData.transactionsScanned}
          </Typography>
        )}

        {/* Listing */}
        <div className="cus-distributions-table">
          <div className="table-with-accordion">
            {
              isLoading
                ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", paddingBlock: 20, textAlign: "center", minHeight: "360px" }}>
                  <CircularProgress size={32} />
                </div>
                : reportData?.soldQtyByTerminal?.length
                  ? <>
                    <div className="cus-table-header">
                      <Typography style={{ flex: 1 }}>Terminal</Typography>
                      <Typography style={{ minWidth: 100, textAlign: 'right' }}>Qty</Typography>
                    </div>
                    {
                      reportData.soldQtyByTerminal.map((terminal, terminalIndex) => {
                        const terminalKey = terminal?.terminalName || terminalIndex;

                        return (
                          <Accordion
                            key={terminalKey}
                            className="accordion-container"
                            expanded={expandedTerminal === terminalKey}
                            onChange={(_, isExpanded) => setExpandedTerminal(isExpanded ? terminalKey : "")}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <div className="accordion-header">
                                <Typography style={{ flex: 1 }}>{terminal?.terminalName || "-"}</Typography>
                                <Typography style={{ minWidth: 100, textAlign: 'right' }}>{terminal?.totalSoldQty || 0}</Typography>
                              </div>
                            </AccordionSummary>
                            <AccordionDetails>
                              <div className="cus-table-header">
                                <Typography style={{ flex: 1 }}>Order</Typography>
                                <Typography style={{ minWidth: 120 }}>Sub Region</Typography>
                                <Typography style={{ minWidth: 100, textAlign: 'right' }}>Qty</Typography>
                              </div>
                              {
                                Object.entries(terminal?.orders || {}).map(([orderId, order]) => (
                                  <Accordion
                                    key={orderId}
                                    className="accordion-container"
                                    expanded={expandedOrder === orderId}
                                    onChange={(_, isExpanded) => setExpandedOrder(isExpanded ? orderId : "")}
                                  >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <div className="accordion-header">
                                        <Typography style={{ flex: 1 }}>{`Order #${orderId}`}</Typography>
                                        <Typography style={{ minWidth: 120 }}>{order?.subRegion || "-"}</Typography>
                                        <Typography style={{ minWidth: 100, textAlign: 'right' }}>{getOrderQty(order?.items)}</Typography>
                                      </div>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <div className="cus-table-header">
                                        <Typography style={{ flex: 1 }}>Product</Typography>
                                        <Typography style={{ minWidth: 100, textAlign: 'right' }}>Qty</Typography>
                                      </div>
                                      {
                                        order?.items?.map((product, productIndex) => (
                                          <div className="cus-table-row" key={productIndex}>
                                            <Typography style={{ flex: 1 }}>{product?.productName || "-"}</Typography>
                                            <Typography style={{ minWidth: 100, textAlign: 'right' }}>{product?.quantity || 0}</Typography>
                                          </div>
                                        ))
                                      }
                                    </AccordionDetails>
                                  </Accordion>
                                ))
                              }
                            </AccordionDetails>
                          </Accordion>
                        )
                      })
                    }
                  </>
                  : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", paddingBlock: 20, textAlign: "center", minHeight: "360px" }}><NoDataFound /></div>
            }
          </div>
        </div>
      </Box>
    </CommonLayout>
  )
}

export default RefillActivityLogs
